import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Form, Formik, FormikHelpers } from 'formik';
import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Text, yup } from '@app/formik';
import { Modal } from '@app/ui/modal';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { COLOR_GATE_UPSERT_ONE } from '../../gql';
import {
  ColorCodeColor,
  ColorGate,
  ColorGateConditionInput,
  ColorGateKind,
  ColorGateUpsertInput,
  RuleField,
  RuleLogic,
} from '../../types';
import { COLOR_CODE_COLOR_VALUES, COLOR_GATE_KIND_VALUES, LOGIC_VALUES } from '../../constants';
import {
  ColorCaseFormValue,
  colorRowError,
  emptyColorCaseRow,
  getGraphQLErrorMessage,
  toColorConditionInput,
  toColorConditionRow,
} from '../../utils';
import { ColorCaseListEditor } from './ColorCaseListEditor';

export interface ColorGateEditorProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly ruleSetVersionId: number;
  readonly gate: ColorGate | null;
  readonly fields: RuleField[];
  readonly onSaved: () => Promise<unknown> | void;
}

interface FormValues {
  kind: ColorGateKind;
  order: string;
  color: ColorCodeColor;
  logic: RuleLogic;
  ruleText: string;
  conditions: ColorCaseFormValue[];
}

const buildInitialValues = (gate: ColorGate | null): FormValues => {
  if (!gate) {
    return {
      kind: ColorGateKind.override,
      order: '1',
      color: ColorCodeColor.red,
      logic: RuleLogic.all,
      ruleText: '',
      conditions: [emptyColorCaseRow()],
    };
  }

  return {
    kind: gate.kind,
    order: String(gate.order),
    color: gate.color,
    logic: gate.logic,
    ruleText: gate.ruleText,
    conditions: gate.conditions.length ? gate.conditions.map(toColorConditionRow) : [emptyColorCaseRow()],
  };
};

// One gate of the evaluation chain (SPEC §3): `override` stops the chain and publishes its own
// colour; `cap` runs after the band and can only ever lower the colour by rank. `conditions[]`
// reuses `ColorCaseRow`/`ColorCaseListEditor` with its own field picker per row (no `points`).
export const ColorGateEditor = ({ open, onClose, ruleSetVersionId, gate, fields, onSaved }: ColorGateEditorProps) => {
  const { t } = useTranslation('common');
  const translate = (key: string) => t(key) ?? key;

  const fieldsByPath = useMemo(() => new Map(fields.map(field => [field.path, field])), [fields]);

  const [upsertGate, { loading }] = useMutation<{ colorGateUpsertOne: ColorGate }>(COLOR_GATE_UPSERT_ONE);

  const initialValues = useMemo(() => buildInitialValues(gate), [gate?.id]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        ruleText: yup.string().required(),
        order: yup
          .string()
          .required()
          .test('isPositiveInteger', translate('rules.colorCoding.validation.orderInvalid'), value => {
            const parsed = Number(value);

            return Number.isInteger(parsed) && parsed >= 1;
          }),
        conditions: yup.array().test({
          name: 'colorConditionRows',
          test(rows, context) {
            const conditions = (rows ?? []) as ColorCaseFormValue[];

            if (!conditions.length) {
              return context.createError({ message: translate('rules.colorCoding.validation.conditionsRequired') });
            }

            return conditions.every(
              row =>
                !colorRowError(row, fieldsByPath.get(row.field), translate, {
                  requireField: true,
                  requirePoints: false,
                }),
            )
              ? true
              : context.createError({ message: translate('rules.editor.validation.conditionInvalid') });
          },
        }),
      }),
    [fieldsByPath, t],
  );

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    const input: ColorGateUpsertInput = {
      ...(gate ? { id: gate.id } : {}),
      ruleSetVersionId,
      kind: values.kind,
      order: Number(values.order),
      color: values.color,
      logic: values.logic,
      ruleText: values.ruleText.trim(),
      conditions: values.conditions
        .map(row => toColorConditionInput(row, fieldsByPath))
        .filter((c): c is ColorGateConditionInput => !!c),
    };

    try {
      await upsertGate({ variables: { input } });
      toastSuccess(t('rules.colorCoding.gate.savedMessage'));
      await onSaved();
      onClose();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
      helpers.setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="md"
      disableBackdropClose
      title={
        gate ? t('rules.colorCoding.gate.editTitle', { gateId: gate.gateId }) : t('rules.colorCoding.gate.newTitle')
      }
    >
      <Formik
        key={gate?.id ?? 'new'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form noValidate>
            {!!gate?.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {gate.note}
              </Typography>
            )}

            <Text name="ruleText" label={t('rules.editor.field.ruleText.label')} multiline rows={2} sx={{ mb: 2 }} />

            {/* Design pass 2026-09-09: Kind / Order / Color share the row equally; Trigger logic keeps its own row. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' } }}>
                <Text
                  select
                  name="kind"
                  label={t('rules.colorCoding.gate.field.kind.label')}
                  options={COLOR_GATE_KIND_VALUES.map(value => ({
                    id: value,
                    name: t(`rules.colorCoding.gate.kind.option.${value}`) ?? value,
                  }))}
                />
              </Box>

              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' } }}>
                <Text name="order" label={t('rules.colorCoding.gate.field.order.label')} />
              </Box>

              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' } }}>
                <Text
                  select
                  name="color"
                  label={t('rules.colorCoding.gate.field.color.label')}
                  options={COLOR_CODE_COLOR_VALUES.map(value => ({
                    id: value,
                    name: t(`rules.colorCoding.color.option.${value}`) ?? value,
                  }))}
                />
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('rules.colorCoding.gate.field.kind.helperText')} {t('rules.colorCoding.gate.field.order.helperText')}
            </Typography>

            <Box sx={{ width: { xs: '100%', sm: 200 }, mb: 2 }}>
              <Text
                select
                name="logic"
                label={t('rules.editor.field.logic.label')}
                options={LOGIC_VALUES.map(value => ({ id: value, name: t(`rules.logic.option.${value}`) ?? '' }))}
              />
            </Box>

            <ColorCaseListEditor
              name="conditions"
              showFieldPicker
              fields={fields}
              showPoints={false}
              disabled={false}
              emptyMessage={t('rules.colorCoding.gate.noConditionsYet') ?? ''}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
              <Button onClick={onClose}>{t('rules.editor.cancelButton')}</Button>
              <LoadingButton loading={isSubmitting || loading} variant="contained" color="primary" type="submit">
                {t('rules.colorCoding.saveButton')}
              </LoadingButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
